'use client';
import { useEffect, useState } from 'react';
import LLInput from '@next-app/components/elements/LLInput/LLInput';
import { selectInitResponse } from '@next-app/lib/features/InitSelectors';
import { useSelector } from 'react-redux';
import {
  AddressAutoFillProps,
  AddressOptions,
  FieldOption,
  PCAControl,
} from '@next-app/interface/AddressAutoFill';
import LoqateAddress from '@next-app/script/LoqateAddress';
import { DeliveryAddressFormFields } from '@next-app/components/Checkout/DeliveryAddressForm/DeliveryAddressFormFields';

declare global {
  interface Window {
    pca: {
      Address: new (
        fields: FieldOption[],
        options: AddressOptions,
      ) => PCAControl;
      fieldMode: {
        SEARCH: number;
        POPULATE: number;
        COUNTRY: number;
      };
    };
  }
}

const AddressAutoFill: React.FC<AddressAutoFillProps> = ({
  idSuffix = '',
  placeholder,
  value,
  onChange,
  onUpdate,
  errorMsg,
  country,
  name,
  onBlurHandler,
  automationId,
}) => {
  const [isPcaLoaded, setIsPcaLoaded] = useState(false);
  const initResponse = useSelector(selectInitResponse);

  //Effects
  // Wait for Loqate library to load, check every 250ms
  useEffect(() => {
    let loqateAddressIntervalId: ReturnType<typeof setTimeout>;
    const checkIfLoqateAddressLoaded = () => {
      if (window.pca) {
        setIsPcaLoaded(true);
        clearInterval(loqateAddressIntervalId);
      }
    };

    loqateAddressIntervalId = setInterval(checkIfLoqateAddressLoaded, 250);
  }, []);

  useEffect(() => {
    if (country !== 'US') {
      return;
    }
    if (initResponse && isPcaLoaded) {
      const formIdentifier = `-autocomplete-address-${idSuffix}`;

      const options: AddressOptions = {
        key: initResponse?.loqateKey,
        search: { countries: 'USA' },
        suppressAutocomplete: false,
        preselect: false,
      };

      const control = new window.pca.Address(
        [
          {
            element: `field${formIdentifier}`,
            field: '{BuildingNumber}\n{Street}',
            mode: window.pca.fieldMode.SEARCH,
          },
        ],
        options,
      );

      control.listen('populate', handlePopulate);

      return () => {
        control.destroy();
      };
    }
  }, [idSuffix, initResponse, isPcaLoaded, country]);

  // Methods
  /**
   * handlePopulate
   * @param address
   */
  const handlePopulate = (address: any) => {
    if (onUpdate) {
      onUpdate(address);
    }
  };

  return (
    <>
      <LLInput
        placeholder={placeholder}
        id={`field-autocomplete-address-${idSuffix}`}
        value={value}
        onChange={onChange}
        name={name}
        autoComplete="off"
        errorMsg={errorMsg}
        maxLength={40}
        onBlur={(e) => onBlurHandler(e)}
        automationId={automationId}
      />
      <LoqateAddress />
    </>
  );
};

export default AddressAutoFill;
